import { useLocation } from "react-router-dom";
import AppSidebar from "./app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../ui/breadcrumb";
import { Separator } from "../ui/separator";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "../ui/sidebar";
import { useAuth } from "../../contexts/authContext";
import Chatbot from "../chatbot/chatbot";
import PendingApprovalsNotification from "../pendingApprovalsNotification";

interface LayoutProps {
  children: React.ReactNode;
}

// Helper function to capitalize first letter
const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Helper function to generate breadcrumbs from pathname
const generateBreadcrumbs = (pathname: string) => {
  // Handle root path
  if (pathname === "/") {
    return [{ label: "Dashboard", href: "/", isCurrentPage: true }];
  }

  // Split path and filter out empty segments
  const segments = pathname.split("/").filter(Boolean);

  const breadcrumbs = segments.map((segment, index) => {
    const href = "/" + segments.slice(0, index + 1).join("/");
    const isCurrentPage = index === segments.length - 1;
    const label = capitalizeFirstLetter(segment);

    return { label, href, isCurrentPage };
  });

  return breadcrumbs;
};

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const breadcrumbs = generateBreadcrumbs(location.pathname);
  const { user } = useAuth();

  // Check if user is admin
  const isAdmin = user?.role === "admin";

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4 flex-1 w-full">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb className="flex-1 min-w-0">
              <BreadcrumbList>
                <BreadcrumbItem className="hidden md:block">
                  <BreadcrumbLink href="#">Payslip Management</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="hidden md:block" />

                {breadcrumbs.map((crumb, index) => (
                  <div key={crumb.href} className="flex items-center">
                    <BreadcrumbItem>
                      {crumb.isCurrentPage ? (
                        <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink href={crumb.href}>
                          {crumb.label}
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                    {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
                  </div>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
            {/* Pending Approvals Badge - Only for Admin */}
            {isAdmin && (
              <div className="ml-auto flex-shrink-0">
                <PendingApprovalsNotification variant="badge" autoRefresh={true} />
              </div>
            )}
          </div>
        </header>
        {children}
      </SidebarInset>

      {/* 🤖 Chatbot - Only visible for admin users */}
      {/* {isAdmin && <Chatbot />} */}
    </SidebarProvider>
  );
}
